import { type IEvent, createSubscriber, getGlobalEventsLogs } from '@cian/events-log';
import { type ILogger } from '@cian/logger/shared';

import { useCallback } from 'react';

import { useCianApiSiteV1GetUserDataByRequestQuery } from '@/packages/query-state/shared/store/monolith-python/cian-api/site/v1/get-user-data-by-request/query';
import { TOPIC_USER, getUserConsumer } from '@/packages/utils/getUserConsumer';
import { noop } from '@/packages/utils/noop';
import { reloadHeader } from '@/packages/utils/reloadHeader';

export type RequestAuthenticationParameters = {
  alert?: 'favorites' | 'chats' | 'hide' | null;
  onUnknownEvent?(event: IEvent<unknown>): Promise<void> | void;
  onDecline?(event: IEvent<unknown>): Promise<void> | void;
  onSuccess?(event: IEvent<unknown>): Promise<void> | void;
};

type AuthenticateEvent = {
  autoRefresh?: boolean;
  alert?: 'favorites' | 'chats' | 'hide' | null;
};

interface IApplicationContext {
  logger: ILogger;
}

type SubscribersHandlerHooks = {
  /**
   * @description Вызывается при любом событии
   */
  onUnknownEvent(event: IEvent<unknown>): Promise<void> | void;
  /**
   * @description Вызывается при авторизации
   */
  onAuthenticated(event: IEvent<unknown>): Promise<void> | void;
  /**
   * @description Вызывается при авторизации или закрытии модального окна
   */
  onSettled(event: IEvent<unknown>): Promise<void> | void;
  /**
   * @description Вызывается при закрытии модального окна
   */
  onDecline(event: IEvent<unknown>): Promise<void> | void;
};

const subscribersHandlerFactory =
  (hooks: SubscribersHandlerHooks) =>
  async (event: IEvent<unknown>): Promise<void> => {
    const { onAuthenticated, onSettled, onDecline, onUnknownEvent } = hooks;

    if (event.type === 'authenticated') {
      await onAuthenticated(event);
      await onSettled(event);
    } else if (event.type === 'authentication_modal_closed') {
      await onDecline(event);
      await onSettled(event);
    } else {
      await onUnknownEvent(event);
    }
  };

type RequestAuthentication = (params?: RequestAuthenticationParameters) => void;

export function useRequestAuthentication(
  { logger }: IApplicationContext,
  parameters: RequestAuthenticationParameters = {},
): RequestAuthentication {
  const { refetch: refetchUser } = useCianApiSiteV1GetUserDataByRequestQuery({ notifyOnChangeProps: [] });

  return useCallback<RequestAuthentication>(
    params => {
      const {
        onDecline = noop,
        onSuccess = noop,
        onUnknownEvent = noop,
        alert = null,
      }: RequestAuthenticationParameters = { ...parameters, ...params };

      const globalLog = getGlobalEventsLogs();
      const consumer = getUserConsumer();

      const userSubscriber = createSubscriber(
        subscribersHandlerFactory({
          async onAuthenticated(event) {
            reloadHeader(logger);

            refetchUser();

            await onSuccess(event);
          },
          onDecline,
          onSettled() {
            consumer.unsubscribe(userSubscriber);
          },
          onUnknownEvent,
        }),
      );

      consumer.subscribe(userSubscriber);

      globalLog.produce<Required<AuthenticateEvent>>(TOPIC_USER, {
        type: 'authenticate',
        value: { autoRefresh: false, alert },
      });
    },
    [parameters, logger, refetchUser],
  );
}
